import React, { useRef, useState } from 'react';
import GeneralEditWizzardBaseSideBySide from '../../../Elements/GeneralEditWizzard/GeneralEditWizzardBaseSideBySideFigma';
import CrudTypesEnum from '../../../Enums/CrudTypesEnum';
import DemoWizardItem from '../../../Domain/DemoWizardItem';
import DemoSideBySideWizardWindowDescription from './DemoSideBySideWizardWindowDescription';

// Stands in for the result of a contract extraction: the values the AI read out of
// the uploaded document. Deliberately imperfect — Category is unresolved, the
// description is verbose and the Budget was missed entirely — so the review step has
// something to correct (and the copy button something empty to copy over).
function buildExtractedItem() {
  const extracted = new DemoWizardItem();
  extracted.Name = 'Aurora Retail Group';
  extracted.CategoryId = '1';
  extracted.Description =
    'Mystery shopping programme covering 42 flagship stores across the DACH region, quarterly waves.';
  // Must stay in the PAST. The current item has no StartDate, so its picker falls back to today - if this
  // date were ever today's, the two columns would coincide and the copy-a-date test could not tell whether
  // the copy had done anything. A past date can never collide; do not "refresh" it to a current one.
  extracted.StartDate = '2026-01-15';
  return extracted;
}

// The Opportunity's current values — mostly empty, as a not-yet-populated checklist would be. The Reference
// is set HERE only, so its `hide` rule comes out differently for the two columns (see the window description).
// The Category deliberately MATCHES the extracted one, so that row's copy button shows up disabled.
function buildCurrentItem() {
  const current = new DemoWizardItem();
  current.Name = 'Aurora Retail';
  current.CategoryId = '1';
  current.Budget = '12000.00';
  current.Reference = 'ARG-2026-114';
  return current;
}

function WizardSideBySideDemoComponent() {
  // Holds the open wizard's mode (null = closed). 'separate' passes a distinct
  // readOnlyItem; 'copy' passes none, so the component falls back to a shallow copy.
  const [openMode, setOpenMode] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState(null);
  // The success path calls clickOnSave via ErrorMessage.SetSuccess(msg, okFn) with NO
  // arguments, so track the latest edited item ourselves (same as the WizardDemo).
  const latestEditedItem = useRef(null);

  return (
    <div>
      <div className='dsg-row'>
        <button
          type='button'
          className='btn primary'
          onClick={() => setOpenMode('separate')}
        >
          <i className='fa-solid fa-table-columns' /> Open side-by-side wizard (two datasets)
        </button>
        <button
          type='button'
          className='btn'
          onClick={() => setOpenMode('copy')}
        >
          <i className='fa-solid fa-clone' /> Open without a readOnlyItem (shallow-copy fallback)
        </button>
      </div>
      {lastSaved ? (
        <p style={{ fontSize: 13, color: 'var(--ink-2)' }}>
          Last "saved" item (in-memory): {JSON.stringify(lastSaved)}
        </p>
      ) : null}
      {openMode ? (
        <GeneralEditWizzardBaseSideBySide
          crudType={CrudTypesEnum.Create}
          editedItem={openMode === 'separate' ? buildCurrentItem() : buildExtractedItem()}
          readOnlyItem={openMode === 'separate' ? buildExtractedItem() : null}
          clickOnCancel={() => setOpenMode(null)}
          clickOnSave={(savedItem) => {
            setLastSaved(savedItem || latestEditedItem.current);
            setOpenMode(null);
          }}
          onChangeEditedItem={(edited) => {
            latestEditedItem.current = edited;
          }}
          windowDescription={DemoSideBySideWizardWindowDescription}
          errorCode='DSG302'
        />
      ) : null}
    </div>
  );
}

export const wizardSideBySideDemo = {
  id: 'edit-wizard-side-by-side',
  title: 'Side-by-side edit wizard (compare & confirm)',
  category: 'Overlays & Editing',
  importPath: 'Elements/GeneralEditWizzard/GeneralEditWizzardBaseSideBySideFigma',
  notes: [
    'Takes an ORDINARY wizard window description — nothing side-by-side-specific is required. It subclasses GeneralEditWizzardBaseFigma/PageFigma and replaces only the body layout, so validation, section nav, footer and the serviceFunction contract are identical.',
    'The read-only panel is ANOTHER instance of the normal GeneralEditWizzardPageFigma, in `embedded` + `readOnly` mode — so both panels render the same inputs through the same renderers. A Select resolves its label on the left too; there is no separate read-only field rendering to keep in sync.',
    '`readOnly` on the page is the whole mechanism: every change handler no-ops, no validityChecker runs, no eraser is offered, and the body is marked `inert` (no clicks, no tab stops). Deliberately NOT `disabled: true` — that swaps each field for an empty PlaceHolderField and the value would disappear.',
    "`embedded` renders the field body only — no OverWindow chrome, no SECTIONS nav, no footer, no section header — so a page can be placed inside another page's layout.",
    'The two datasets need not match. Omit `readOnlyItem` and it defaults to a SHALLOW COPY of the edited item — the panels start identical but never share a reference, so editing the right side never changes what the left shows (try the second button).',
    'Per-field `hideOnReadOnlySide` / `hideOnEditSide` are ordinary hide rules (boolean or `(editedItem) => boolean`) that each apply on one side only — the field is not validated, not counted and not shown on that side. See the confirmation toggle on step 2, which exists only on the editable side.',
    'A COPY BUTTON sits between the columns on every row whose field is on screen in BOTH — it overwrites the editable field with the read-only value UNCONDITIONALLY, empty/null/undefined included, so it also clears a value the extraction invented (try Budget on step 2: the "extraction" missed it). One-sided rows keep their place and simply have no button.',
    "A row whose two sides ALREADY hold the same value gets its copy button DISABLED (Category on step 1) — the enabled arrows are therefore a map of what actually differs, and there is nothing to gain from copying a value that is already there. Equality is judged as the user sees it in the two inputs: everything that renders empty (undefined, null, '', an empty array) counts as the same, a number equals its string form, and Dates/arrays/objects compare by content. Edit the field and its arrow lights up again.",
    'Rows line up: where one column hides a field the OTHER still shows (a one-sided rule, or a `hide` rule that reads the item and comes out differently for the two datasets — see Client Reference on step 1), the hiding side keeps a blank, non-focusable row so nothing below it slides out of line. A field hidden on BOTH sides leaves no row anywhere: open the demo without a readOnlyItem and Client Reference is simply gone from both columns. The buttons are rendered inside the editable field cells and positioned into the gutter, which is what keeps each one exactly on its own row while sitting halfway between the two cards.',
    'Optional window-description statics `readOnlySideLabel` / `editSideLabel` set the panel headers.',
    "Remaining limit: the columns are still separate subtrees, so anything that changes ONE side's height in place — a validation message under the right-hand input — shifts that side by that much.",
  ],
  snippet:
    "<GeneralEditWizzardBaseSideBySide\n  crudType={CrudTypesEnum.Create}\n  editedItem={this.state.checklist}\n  readOnlyItem={this.state.extractedFromContract}\n  clickOnCancel={() => this.setState({ shownPanel: ShownPanelsEnum.None })}\n  clickOnSave={this.refresh}\n  windowDescription={ImportFromContractWindowDescription}\n  errorCode={'XX123'}\n/>",
  render: WizardSideBySideDemoComponent,
};
